import React, { useMemo } from 'react'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { SevvThemeChrome, SevvThemeTokens } from '@/theme/sevvThemeTokenModel'
import { DEFAULT_SEVV_THEME_TOKENS } from '@/theme/sevvThemeTokenModel'
import { ColorPicker } from './ColorPicker'
import {
  type ActionsBackgroundMode,
  type ChromeBorderPreset,
  type TitleBackgroundPickerMode,
  buildActionsBackground,
  buildChromeBorder,
  buildTitleBackground,
  detectActionsBackgroundMode,
  detectTitleBorderPreset,
  formatBackdropRgba,
  parseBackdropRgba,
  parseCustomBorder,
  parseTitleBackground,
} from '../utils/shellModalChromeUtils'

export interface ShellModalChromePickersProps {
  tone: 'light' | 'dark'
  designTokens: SevvThemeTokens
  effectivePalette: SevvThemeTokens['palette']
  /** Resolved overlay for this tone (merged light + dark). */
  effectiveBackdrop: string
  effectiveTitleColor: string
  onPatch: (partial: Partial<SevvThemeChrome>, tone: 'light' | 'dark') => void
  hasDarkOverride: (key: keyof SevvThemeChrome) => boolean
  onClearDarkOverride: (key: keyof SevvThemeChrome) => void
  disabled?: boolean
}

function storedKey(
  tone: 'light' | 'dark',
  designTokens: SevvThemeTokens,
  key: keyof SevvThemeChrome
): string | undefined {
  if (tone === 'light') {
    return designTokens.chrome?.[key] as string | undefined
  }
  return designTokens.chromeDark?.[key] as string | undefined
}

export const ShellModalChromePickers: React.FC<ShellModalChromePickersProps> = ({
  tone,
  designTokens,
  effectivePalette,
  effectiveBackdrop,
  effectiveTitleColor,
  onPatch,
  hasDarkOverride,
  onClearDarkOverride,
  disabled = false,
}) => {
  const defaultOverlay = DEFAULT_SEVV_THEME_TOKENS.chrome!.overlayBackdrop
  const p = effectivePalette

  const patch = (partial: Partial<SevvThemeChrome>) => onPatch(partial, tone)

  const titleBgRaw = storedKey(tone, designTokens, 'dialogTitleBackground')
  const titleBorderRaw = storedKey(tone, designTokens, 'dialogTitleBorderBottom')
  const actionsBgRaw = storedKey(tone, designTokens, 'dialogActionsBackground')
  const actionsBorderRaw = storedKey(tone, designTokens, 'dialogActionsBorderTop')
  const titleColorRaw = storedKey(tone, designTokens, 'dialogTitleColor')

  const titleBgParsed = useMemo(() => parseTitleBackground(titleBgRaw, p), [titleBgRaw, p])
  const titleBorderPreset = useMemo(() => detectTitleBorderPreset(titleBorderRaw), [titleBorderRaw])
  const actionsBorderPreset = useMemo(() => detectTitleBorderPreset(actionsBorderRaw), [actionsBorderRaw])
  const actionsBgParsed = useMemo(
    () => detectActionsBackgroundMode(actionsBgRaw, p.primary),
    [actionsBgRaw, p.primary]
  )
  const customTitleBorder = useMemo(() => parseCustomBorder(titleBorderRaw), [titleBorderRaw])
  const customActionsBorder = useMemo(() => parseCustomBorder(actionsBorderRaw), [actionsBorderRaw])

  const backdropParsed = useMemo(() => {
    const raw =
      tone === 'light'
        ? designTokens.chrome?.overlayBackdrop ?? defaultOverlay
        : designTokens.chromeDark?.overlayBackdrop !== undefined
          ? designTokens.chromeDark.overlayBackdrop
          : designTokens.chrome?.overlayBackdrop ?? defaultOverlay
    return parseBackdropRgba(raw)
  }, [tone, designTokens.chrome, designTokens.chromeDark, defaultOverlay])

  const setBackdrop = (hex: string, opacity: number) => {
    patch({ overlayBackdrop: formatBackdropRgba(hex, opacity) })
  }

  const borderPresetToggle = (
    value: ChromeBorderPreset,
    key: 'dialogTitleBorderBottom' | 'dialogActionsBorderTop',
    template: 'title' | 'actions',
    customW: number,
    customC: string
  ) => {
    const built = buildChromeBorder(value, customW, customC, template)
    patch({ [key]: built } as Partial<SevvThemeChrome>)
  }

  const ClearDarkBtn = ({ k }: { k: keyof SevvThemeChrome }) =>
    tone === 'dark' && hasDarkOverride(k) ? (
      <Button size="small" variant="text" disabled={disabled} sx={{ mt: 1, display: 'block' }} onClick={() => onClearDarkOverride(k)}>
        Use light shell default
      </Button>
    ) : null

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Modal overlay (backdrop)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Dimmed layer behind dialogs. Choose a color and opacity—no raw CSS.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <ColorPicker
            label="Backdrop base"
            value={backdropParsed.hex}
            onChange={(hex) => setBackdrop(hex, backdropParsed.opacity)}
          />
          <Box sx={{ minWidth: 200, flex: 1, px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Opacity ({Math.round(backdropParsed.opacity * 100)}%)
            </Typography>
            <Slider
              size="small"
              min={0}
              max={1}
              step={0.05}
              value={backdropParsed.opacity}
              onChange={(_, v) => setBackdrop(backdropParsed.hex, v as number)}
              disabled={disabled}
              valueLabelDisplay="auto"
              valueLabelFormat={(x) => `${Math.round(x * 100)}%`}
            />
          </Box>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontFamily: 'monospace' }}>
          {effectiveBackdrop}
        </Typography>
        <ClearDarkBtn k="overlayBackdrop" />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Dialog title text color
        </Typography>
        <ColorPicker
          label="Title & icon color"
          value={titleColorRaw?.trim() ? titleColorRaw : effectiveTitleColor}
          onChange={(v) => patch({ dialogTitleColor: v })}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Default is high-contrast white on the brand title bar; use text primary if the bar is flat paper.
        </Typography>
        <ClearDarkBtn k="dialogTitleColor" />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Dialog title bar background
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Brand gradient follows <strong>Primary</strong> and <strong>Primary light</strong> from the palette for this mode.
        </Typography>
        <PresetToggleRow
          value={titleBgParsed.mode}
          disabled={disabled}
          options={[
            { value: 'default', label: 'Brand gradient' },
            { value: 'solid', label: 'Solid' },
            { value: 'gradient', label: 'Two-color gradient' },
          ]}
          onChange={(m) => {
            const next = buildTitleBackground(m as TitleBackgroundPickerMode, titleBgParsed.solid, titleBgParsed.gFrom, titleBgParsed.gTo)
            patch({ dialogTitleBackground: next })
          }}
        />
        {titleBgParsed.mode === 'solid' && (
          <Box sx={{ mt: 2 }}>
            <ColorPicker
              label="Title bar fill"
              value={titleBgParsed.solid}
              onChange={(v) => patch({ dialogTitleBackground: buildTitleBackground('solid', v, titleBgParsed.gFrom, titleBgParsed.gTo) })}
            />
          </Box>
        )}
        {titleBgParsed.mode === 'gradient' && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <ColorPicker
                label="Gradient start"
                value={titleBgParsed.gFrom}
                onChange={(v) =>
                  patch({
                    dialogTitleBackground: buildTitleBackground('gradient', titleBgParsed.solid, v, titleBgParsed.gTo),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ColorPicker
                label="Gradient end"
                value={titleBgParsed.gTo}
                onChange={(v) =>
                  patch({
                    dialogTitleBackground: buildTitleBackground('gradient', titleBgParsed.solid, titleBgParsed.gFrom, v),
                  })
                }
              />
            </Grid>
          </Grid>
        )}
        <ClearDarkBtn k="dialogTitleBackground" />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Title bar bottom border
        </Typography>
        <BorderPresetBlock
          preset={titleBorderPreset}
          customWidth={customTitleBorder.width}
          customColor={customTitleBorder.color}
          disabled={disabled}
          onPresetChange={(preset) =>
            borderPresetToggle(preset, 'dialogTitleBorderBottom', 'title', customTitleBorder.width, customTitleBorder.color)
          }
          onCustomWidth={(w) =>
            borderPresetToggle('custom', 'dialogTitleBorderBottom', 'title', w, customTitleBorder.color)
          }
          onCustomColor={(c) =>
            borderPresetToggle('custom', 'dialogTitleBorderBottom', 'title', customTitleBorder.width, c)
          }
        />
        <ClearDarkBtn k="dialogTitleBorderBottom" />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Dialog footer (actions row) background
        </Typography>
        <PresetToggleRow
          value={actionsBgParsed.mode}
          disabled={disabled}
          options={[
            { value: 'default', label: 'Brand default' },
            { value: 'primaryTint', label: 'Primary wash' },
            { value: 'solid', label: 'Solid' },
          ]}
          onChange={(m) => {
            const mode = m as ActionsBackgroundMode
            const built = buildActionsBackground(mode, p.primary, actionsBgParsed.opacity, actionsBgParsed.solid)
            patch({ dialogActionsBackground: built })
          }}
        />
        {actionsBgParsed.mode === 'primaryTint' && (
          <Box sx={{ mt: 2, maxWidth: 360 }}>
            <Typography variant="caption" color="text.secondary">
              Wash strength ({Math.round(actionsBgParsed.opacity * 100)}%)
            </Typography>
            <Slider
              size="small"
              min={0.02}
              max={0.35}
              step={0.01}
              value={actionsBgParsed.opacity}
              onChange={(_, v) =>
                patch({
                  dialogActionsBackground: buildActionsBackground('primaryTint', p.primary, v as number, actionsBgParsed.solid),
                })
              }
              disabled={disabled}
              valueLabelDisplay="auto"
              valueLabelFormat={(x) => `${Math.round(x * 100)}%`}
            />
          </Box>
        )}
        {actionsBgParsed.mode === 'solid' && (
          <Box sx={{ mt: 2 }}>
            <ColorPicker
              label="Footer background"
              value={actionsBgParsed.solid}
              onChange={(v) => patch({ dialogActionsBackground: buildActionsBackground('solid', p.primary, 0.08, v) })}
            />
          </Box>
        )}
        <ClearDarkBtn k="dialogActionsBackground" />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Footer top border
        </Typography>
        <BorderPresetBlock
          preset={actionsBorderPreset}
          customWidth={customActionsBorder.width}
          customColor={customActionsBorder.color}
          disabled={disabled}
          onPresetChange={(preset) =>
            borderPresetToggle(preset, 'dialogActionsBorderTop', 'actions', customActionsBorder.width, customActionsBorder.color)
          }
          onCustomWidth={(w) =>
            borderPresetToggle('custom', 'dialogActionsBorderTop', 'actions', w, customActionsBorder.color)
          }
          onCustomColor={(c) =>
            borderPresetToggle('custom', 'dialogActionsBorderTop', 'actions', customActionsBorder.width, c)
          }
        />
        <ClearDarkBtn k="dialogActionsBorderTop" />
      </Box>
    </Stack>
  )
}

function PresetToggleRow<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, v) => v != null && onChange(v as T)}
      disabled={disabled}
      sx={{ flexWrap: 'wrap', gap: 0.5 }}
    >
      {options.map((o) => (
        <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none', px: 1.5 }}>
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

function BorderPresetBlock({
  preset,
  customWidth,
  customColor,
  disabled,
  onPresetChange,
  onCustomWidth,
  onCustomColor,
}: {
  preset: ChromeBorderPreset
  customWidth: number
  customColor: string
  disabled?: boolean
  onPresetChange: (p: ChromeBorderPreset) => void
  onCustomWidth: (w: number) => void
  onCustomColor: (c: string) => void
}) {
  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={preset}
        onChange={(_, v) => v != null && onPresetChange(v as ChromeBorderPreset)}
        disabled={disabled}
        sx={{ flexWrap: 'wrap', gap: 0.5 }}
      >
        <ToggleButton value="default" sx={{ textTransform: 'none' }}>
          Brand default
        </ToggleButton>
        <ToggleButton value="divider" sx={{ textTransform: 'none' }}>
          Divider token
        </ToggleButton>
        <ToggleButton value="primaryTint" sx={{ textTransform: 'none' }}>
          Primary tint
        </ToggleButton>
        <ToggleButton value="custom" sx={{ textTransform: 'none' }}>
          Custom
        </ToggleButton>
      </ToggleButtonGroup>
      {preset === 'custom' && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="shell-border-width">Width</InputLabel>
            <Select
              labelId="shell-border-width"
              label="Width"
              value={customWidth}
              onChange={(e) => onCustomWidth(Number(e.target.value))}
              disabled={disabled}
            >
              {[1, 2, 3, 4].map((w) => (
                <MenuItem key={w} value={w}>
                  {w}px
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ColorPicker label="Border color" value={customColor} onChange={onCustomColor} />
        </Stack>
      )}
    </Stack>
  )
}
